import { useTranslation } from '@/contexts/i18n-context';
import { Button } from '@/components/button';
import styles from './save-progress-button.module.css';
import clsx from 'clsx';
import { useState } from 'react';
import { Modal, ModalBody, ModalButtonContainer } from '@/components/modal/modal';
import { ModalButtons } from '../modal/modal-buttons';
import { usePostSubmitNavigation } from '@/hooks/use-post-submit-navigation';
import { useVisitNavigation } from '@/contexts/visit-navigation-context';

export interface SaveProgressButtonProps {
  onSaveClick?: () => void;
  className?: string;
  showModal?: boolean;
}

const translationBase = 'visit.saveProgress';

export function SaveProgressButton(props: SaveProgressButtonProps) {
  const { editMode } = useVisitNavigation();
  return editMode ? <ReturnToReviewButton {...props} /> : <SaveAndContinueLaterButton {...props} />;
}

function SaveAndContinueLaterButton({
  className,
  onSaveClick,
  showModal: showLossOfProgressModal,
}: SaveProgressButtonProps) {
  const { t } = useTranslation();
  const [isContinueLaterModalOpen, setIsContinueLaterModalOpen] = useState(false);
  const { navigatePostSubmit } = usePostSubmitNavigation();

  const handleConfirm = () => {
    setIsContinueLaterModalOpen(false);

    // For the "Save and Continue Later" button, we want to always show the modal regardless of the property passed in.
    // The showModal property means that there are some changes made (isDirty), but the changes cannot be saved yet (!isValid).
    // That's why we don't want to save the changes if the showModal (showLossOfProgressModal) is true.
    if (!showLossOfProgressModal && onSaveClick) {
      // The navigation should be handled by the passed onSaveClick callback
      onSaveClick();
      return;
    }

    navigatePostSubmit({ isIncomplete: true });
  };

  return (
    <>
      <Button
        className={clsx(className, styles.saveProgressButton)}
        variant="link"
        onClick={() => setIsContinueLaterModalOpen(true)}
      >
        {t('common.saveAndContinueLater')}
      </Button>

      <Modal isOpen={isContinueLaterModalOpen} onClose={() => setIsContinueLaterModalOpen(false)}>
        <h2 className="h4">
          {t(
            `${translationBase}.saveAndContinueLater.title.${showLossOfProgressModal ? 'lossOfProgress' : 'saveChanges'}`,
          )}
        </h2>
        <ModalBody>{t(`${translationBase}.saveAndContinueLater.body`)}</ModalBody>
        <ModalButtonContainer>
          <Button variant="outlined" onClick={() => setIsContinueLaterModalOpen(false)}>
            {t(`${translationBase}.saveAndContinueLater.cancel`)}
          </Button>
          <Button onClick={handleConfirm}>
            {t(`${translationBase}.saveAndContinueLater.confirm`)}
          </Button>
        </ModalButtonContainer>
      </Modal>
    </>
  );
}

function ReturnToReviewButton({ className, onSaveClick, showModal }: SaveProgressButtonProps) {
  const { t } = useTranslation();
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const { navigatePostSubmit } = usePostSubmitNavigation();

  const handleOnClick = () => {
    if (showModal) {
      setIsReviewModalOpen(true);
      return;
    }
    navigatePostSubmit();
  };

  const handleConfirm = (intent: 'save' | 'discard') => {
    setIsReviewModalOpen(false);
    if (onSaveClick && intent === 'save') {
      // The navigation should be handled by the passed onSaveClick callback
      onSaveClick();
    } else {
      navigatePostSubmit();
    }
  };

  return (
    <>
      <Button
        className={clsx(className, styles.saveProgressButton)}
        variant="link"
        onClick={handleOnClick}
        rightIcon="arrow_forward"
      >
        {t('common.returnToReview')}
      </Button>

      <Modal isOpen={isReviewModalOpen} onClose={() => setIsReviewModalOpen(false)}>
        <h2 className="h4">{t(`${translationBase}.returnToReviewTitle`)}</h2>
        <ModalButtons
          cancel={{
            text: t('common.no'),
            onClick: () => handleConfirm('discard'),
          }}
          confirm={{
            text: t('common.yes'),
            onClick: () => handleConfirm('save'),
          }}
        />
      </Modal>
    </>
  );
}
